import React from 'react';

import {Owner} from 'auth/PermissionConstants';
import {isOrganization, isUser} from 'constants/UserConstants';
import {
  ReviewLifecyclesForOrganizationQuery,
  ReviewLifecyclesForOrganizationQueryVariables,
} from 'create-components/__generated/ReviewLifecyclesForOrganizationQuery';
import {
  ReviewLifecyclesForUserQuery,
  ReviewLifecyclesForUserQueryVariables,
} from 'create-components/__generated/ReviewLifecyclesForUserQuery';
import {
  REVIEW_LIFECYCLES_FOR_ORGANIZATION_QUERY,
  REVIEW_LIFECYCLES_FOR_USER_QUERY,
} from 'create-components/ReviewProcessAndCollaboratorsSection';
import {BenchlingQueryResult, QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ReviewProcessSelector, {
  generateReviewProcessSelectorOptions,
} from 'notebook/reviews/review-lifecycles/ReviewProcessSelector';
import notifications from 'notifications';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {OptionData} from 'ui-library/menus/MenuConstants';
import * as logging from 'util/Logging';
import sortedBy from 'util/sortedBy';

type OrganizationGraphQL = ExtractByTypename<
  ReviewLifecyclesForOrganizationQuery['organization'],
  'OrganizationNode'
>;

interface Props {
  owner: Owner;
  selectedReviewLifecycleIds: Array<string>;
  selectReviewLifecycleId: (reviewLifecycleId: string) => void;
  removeReviewLifecycleId: (reviewLifecycleId: string) => void;
  isReadOnly: boolean;
}

function buildOptions(
  organizationsWithReviewLifecycles: Array<OrganizationGraphQL>,
  selectedReviewLifecycleIds: Array<string>
): Array<OptionData> {
  const organizationsWithAvailableReviewLifecycles = organizationsWithReviewLifecycles
    .map((organization) => ({
      ...organization,
      reviewLifecycles: organization.reviewLifecycles.filter((rlc) => rlc.archiveRecord == null),
    }))
    .filter((organization) => organization.reviewLifecycles.length > 0);

  const toOptions = (reviewLifecycles: OrganizationGraphQL['reviewLifecycles']): Array<OptionData> =>
    generateReviewProcessSelectorOptions({
      reviewLifecycleIdAndNames: reviewLifecycles.map(({currentVersion, id}) => ({
        id,
        name: currentVersion.name,
      })),
      selectedReviewLifecycleIds,
      isMulti: true,
    });

  // Group options under organization headers when the owner spans multiple orgs (mirrors the project
  // selector, which shows headers whenever there is more than one possible org, even if only one has
  // available review lifecycles); otherwise render a flat list.
  if (organizationsWithReviewLifecycles.length > 1) {
    return sortedBy(organizationsWithAvailableReviewLifecycles, ['name']).flatMap((organization) => [
      {name: organization.name, value: '', isHeader: true} satisfies OptionData,
      ...toOptions(organization.reviewLifecycles),
    ]);
  }

  return toOptions(
    organizationsWithAvailableReviewLifecycles.flatMap((organization) => organization.reviewLifecycles)
  );
}

function renderReviewProcessSection<TData>(
  result: BenchlingQueryResult<TData>,
  getOrganizationsWithReviewLifecycles: (data: TData) => Array<OrganizationGraphQL> | null,
  {
    selectedReviewLifecycleIds,
    selectReviewLifecycleId,
    removeReviewLifecycleId,
    isReadOnly,
  }: Omit<Props, 'owner'>
): JSX.Element | null {
  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR) {
    notifications.error('There was an error loading review processes. Please refresh the page');
    return null;
  }

  const organizationsWithReviewLifecycles = getOrganizationsWithReviewLifecycles(result.data);
  if (organizationsWithReviewLifecycles == null) {
    logging.error('User was unable to query their own review lifecycle data');
    return null;
  }

  const options = buildOptions(organizationsWithReviewLifecycles, selectedReviewLifecycleIds);
  if (options.length === 0) {
    return null;
  }

  return (
    <ReviewProcessSelector
      includeAuditorBasedReviewOption={false}
      isMulti={true}
      showWarnings={false}
      className="well"
      options={options}
      selectedReviewLifecycleIds={selectedReviewLifecycleIds}
      selectReviewLifecycleId={selectReviewLifecycleId}
      removeReviewLifecycleId={removeReviewLifecycleId}
      isReadOnly={isReadOnly}
    />
  );
}

function ReviewProcessSectionForOrganization({owner, ...rest}: Props): JSX.Element | null {
  const result = useBenchlingQuery<
    ReviewLifecyclesForOrganizationQuery,
    ReviewLifecyclesForOrganizationQueryVariables
  >(REVIEW_LIFECYCLES_FOR_ORGANIZATION_QUERY, {variables: {orgId: owner.id}});

  return renderReviewProcessSection(
    result,
    ({organization}) => (isInaccessible(organization) ? null : [organization]),
    rest
  );
}

function ReviewProcessSectionForUser({owner, ...rest}: Props): JSX.Element | null {
  const result = useBenchlingQuery<ReviewLifecyclesForUserQuery, ReviewLifecyclesForUserQueryVariables>(
    REVIEW_LIFECYCLES_FOR_USER_QUERY,
    {variables: {userId: owner.id}}
  );

  return renderReviewProcessSection(
    result,
    ({user}) => (isInaccessible(user) ? null : user.organizations),
    rest
  );
}

/**
 * Renders the Review Process selector for a template collection's settings modal. Fetches the review
 * lifecycles available to the collection's owner and mounts the shared ReviewProcessSelector.
 *
 * RLCs are always owned by an Organization (never a user), so the branch below keys off the
 * collection's owner type, not RLC ownership.
 * - An org-owned collection reads RLCs from its single org
 * - A user-owned collection has no RLCs of its own, so we union the RLCs across every org the user
 * belongs to.
 */
export default function TemplateCollectionReviewProcessSection(props: Props): JSX.Element | null {
  if (isOrganization(props.owner)) {
    return <ReviewProcessSectionForOrganization {...props} />;
  } else if (isUser(props.owner)) {
    return <ReviewProcessSectionForUser {...props} />;
  }
  return null;
}
